import React, { useEffect } from 'react';
import { Mail, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Reveal } from '../components/Reveal';
import { brands } from '../mockData';

const SOCIAL_ICONS = {
  instagram: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className="w-5 h-5">
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  ),
  twitter: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  ),
  linkedin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className="w-5 h-5">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect x="2" y="9" width="4" height="12" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  ),
  website: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className="w-5 h-5">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  )
};

const SOCIAL_LABELS = {
  instagram: 'Instagram',
  twitter: 'X / Twitter',
  linkedin: 'LinkedIn',
  website: 'Website'
};

const BrandCard = ({ brand }) => (
  <div
    data-testid={`connect-card-${brand.slug}`}
    className="bg-white border border-black/5 hover:border-[#B8960C]/30 transition-all duration-500 group h-full"
  >
    <div className="relative overflow-hidden h-56 md:h-64">
      <img
        src={brand.image}
        alt={brand.name}
        className="w-full h-full object-contain bg-white transition-transform duration-700 group-hover:scale-105 opacity-90 group-hover:opacity-100"
      />
    </div>
    <div className="p-8">
      <h3
        data-testid={`connect-brand-name-${brand.slug}`}
        className="font-heading text-2xl font-light text-[#1A1A1A] uppercase mb-1"
        style={{ letterSpacing: '0.08em' }}
      >
        {brand.name}
      </h3>
      <p className="text-xs text-[#B8960C] font-light tracking-wide mb-4">
        {brand.tagline}
      </p>
      {brand.email && (
        <a
          data-testid={`connect-email-${brand.slug}`}
          href={`mailto:${brand.email}`}
          className="inline-flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#B8960C] transition-colors duration-300 font-light mb-4"
        >
          <Mail size={14} strokeWidth={1.2} className="text-[#B8960C]/50" />
          {brand.email}
        </a>
      )}
      {Object.keys(brand.socials).length > 0 && (
        <div className="flex items-center gap-4 pt-4 border-t border-black/5">
          {Object.entries(brand.socials).map(([platform, url]) => (
            <a
              key={platform}
              data-testid={`connect-social-${brand.slug}-${platform}`}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="group/icon flex items-center gap-2 text-[#9CA3AF] hover:text-[#B8960C] transition-colors duration-300"
              title={SOCIAL_LABELS[platform]}
            >
              {SOCIAL_ICONS[platform]}
              <span className="text-[10px] uppercase tracking-[0.15em] font-medium hidden lg:inline">
                {SOCIAL_LABELS[platform]}
              </span>
            </a>
          ))}
        </div>
      )}
    </div>
  </div>
);

const ConnectPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white pt-24 md:pt-32">
      <section data-testid="connect-header" className="px-6 md:px-12 lg:px-24 max-w-7xl mx-auto mb-16 md:mb-24">
        <Reveal>
          <button
            data-testid="connect-back-button"
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.2em] text-[#9CA3AF] hover:text-[#B8960C] transition-colors duration-300 mb-12"
          >
            <ArrowLeft size={14} strokeWidth={1.5} />
            Back to Home
          </button>
          <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-[#B8960C] mb-6">Get In Touch</p>
          <h1
            data-testid="connect-page-heading"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-[#1A1A1A] uppercase leading-tight"
            style={{ letterSpacing: '0.1em' }}
          >
            Connect With Us
          </h1>
          <div className="w-16 h-px bg-[#B8960C] mt-8 mb-8 opacity-40"></div>
          <p className="text-base text-[#6B7280] font-light leading-relaxed max-w-2xl">
            Each of our brands operates with its own identity and team.
            Reach out directly to the brand that best fits your needs.
          </p>
        </Reveal>
      </section>

      <section data-testid="connect-brands" className="px-6 md:px-12 lg:px-24 max-w-7xl mx-auto pb-20 md:pb-28">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {brands.map((brand, index) => (
            <Reveal key={brand.id} delay={index * 0.08}>
              <BrandCard brand={brand} />
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="connect-press-media" className="px-6 md:px-12 lg:px-24 max-w-7xl mx-auto pb-24 md:pb-32 lg:pb-48">
        <Reveal>
          <div className="border-t border-black/5 pt-16">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
              <div>
                <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-[#B8960C] mb-4">
                  Press & Media Requests
                </p>
                <a
                  data-testid="press-media-email-link"
                  href="mailto:Info@Hinton-media.com"
                  className="text-lg md:text-xl text-[#1A1A1A] font-light hover:text-[#B8960C] transition-colors duration-300"
                >
                  Info@Hinton-media.com
                </a>
              </div>
              <div className="md:text-right">
                <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-[#B8960C] mb-4">
                  Head Office
                </p>
                <p className="text-sm text-[#6B7280] font-light leading-relaxed">
                  124 City Road<br />
                  London, England<br />
                  EC1V 2NX
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default ConnectPage;
